// frontend/src/services/auditoriaService.js
import axios from 'axios';

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para el token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const auditoriaService = {
  /**
   * Obtener logs de auditoría con filtros
   */
  getLogs: async (params = {}) => {
    try {
      // Construir parámetros de consulta
      const queryParams = new URLSearchParams();
      
      // Paginación
      queryParams.append('page', params.page || 1);
      queryParams.append('limit', params.limit || 50);
      
      // Filtros
      if (params.usuario_id) queryParams.append('usuario_id', params.usuario_id);
      if (params.accion) queryParams.append('accion', params.accion);
      if (params.tabla) queryParams.append('tabla', params.tabla);
      if (params.fecha_inicio) queryParams.append('fecha_inicio', params.fecha_inicio);
      if (params.fecha_fin) queryParams.append('fecha_fin', params.fecha_fin);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await apiClient.get(`/auditoria/logs?${queryParams.toString()}`);
      return response;
    } catch (error) {
      console.error('Error al obtener logs de auditoría:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de auditoría
   */
  getEstadisticas: async (dias = 30) => {
    try {
      const response = await apiClient.get(`/auditoria/estadisticas?dias=${dias}`);
      return response;
    } catch (error) {
      console.error('Error al obtener estadísticas de auditoría:', error);
      throw error;
    }
  },

  /**
   * Obtener detalle de un log específico
   */
  getDetalleLog: async (id) => {
    try {
      if (!id) {
        throw new Error('ID del log es requerido');
      }
      
      const response = await apiClient.get(`/auditoria/logs/${id}`);
      return response;
    } catch (error) {
      console.error(`Error al obtener detalle del log ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener lista de usuarios para filtros
   */
  getUsuariosParaFiltro: async () => {
    try {
      const response = await apiClient.get('/auditoria/usuarios');
      return response;
    } catch (error) {
      console.error('Error al obtener usuarios para filtro:', error);
      throw error;
    }
  },

  /**
   * Obtener lista de tablas para filtros
   */
  getTablasParaFiltro: async () => {
    try {
      const response = await apiClient.get('/auditoria/tablas');
      return response;
    } catch (error) {
      console.error('Error al obtener tablas para filtro:', error);
      throw error;
    }
  },

  /**
   * Exportar logs a CSV (si se implementa en el futuro)
   */
  exportarLogs: async (filtros = {}) => {
    try {
      const queryParams = new URLSearchParams(filtros);
      const response = await apiClient.get(`/auditoria/export?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auditoria_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return response;
    } catch (error) {
      console.error('Error al exportar logs:', error);
      throw error;
    }
  },

  /**
   * Buscar logs con texto libre
   */
  buscarLogs: async (texto, filtros = {}) => {
    try {
      const params = {
        ...filtros,
        search: texto,
        page: filtros.page || 1,
        limit: filtros.limit || 50
      };
      
      return await this.getLogs(params);
    } catch (error) {
      console.error('Error al buscar logs:', error);
      throw error;
    }
  },

  /**
   * Obtener actividad reciente de un usuario específico
   */
  getActividadUsuario: async (usuarioId, limite = 20) => {
    try {
      const params = {
        usuario_id: usuarioId,
        limit: limite,
        page: 1
      };
      
      const response = await this.getLogs(params);
      return response;
    } catch (error) {
      console.error(`Error al obtener actividad del usuario ${usuarioId}:`, error);
      throw error;
    }
  },

  /**
   * Obtener logs de una tabla específica
   */
  getLogsPorTabla: async (tabla, limite = 50) => {
    try {
      const params = {
        tabla: tabla,
        limit: limite,
        page: 1
      };
      
      const response = await this.getLogs(params);
      return response;
    } catch (error) {
      console.error(`Error al obtener logs de la tabla ${tabla}:`, error);
      throw error;
    }
  }
};

export default auditoriaService;